import { type FunPaymentItem, isFinalFunPaymentState } from "@decibeltrade/utils";

import { FUN_IMPORTED_PAYMENTS_KEY } from "@/constants/local-storage-keys";
import { boundedKeySet } from "@/hooks/storage/bounded-key-set";
import { MS_PER_DAY } from "@/utils/time";
import type { DistributiveOmit } from "@/utils/types";

import type { TransferProgressData, TransferStep } from "./transfer-progress-card";

export type FunDepositTransfer = Extract<TransferProgressData, { type: "fun-deposit" }>;
type XchainDepositTransfer = Extract<TransferProgressData, { type: "xchain-deposit" }>;
export type NewFunCard = DistributiveOmit<FunDepositTransfer, "id">;

/**
 * Per owner wallet: which items already became a card. Keyed per `(id, provisional)`, so dismissing
 * a provisional item never hides the evidence that later backs the same id.
 */
export const funImportedPayments = boundedKeySet(FUN_IMPORTED_PAYMENTS_KEY, 200);

/** A finished item first seen on this device only gets a card within this window. */
export const FUN_IMPORT_WINDOW_MS = 7 * MS_PER_DAY;

export function funImportKey(id: string, provisional: boolean): string {
  return `${id}:${provisional ? "p" : "e"}`;
}

function idsOf(item: FunPaymentItem): string[] {
  return [...new Set([item.id, ...item.aliases])];
}

export function funCardStep(item: FunPaymentItem, previous?: TransferStep): TransferStep {
  if (previous === "completed") return "completed";
  if (item.state === "done") return "available";
  return isFinalFunPaymentState(item.state) ? "transfer-failed" : "confirming";
}

function amountOf(item: FunPaymentItem): number {
  const value = Number(item.delivery?.amountOutFormatted ?? item.amount.paidOutUsdc ?? 0);
  return Number.isFinite(value) ? value : 0;
}

/** Every field the card derives from the item, i.e. everything but identity and slot. */
function projectedFields(item: FunPaymentItem) {
  const delivery = item.delivery;
  return {
    paymentId: item.id,
    projection: item,
    amount: amountOf(item),
    deliveryId: delivery?.id,
    destinationAssetKey: delivery?.destinationAssetKey,
    destinationSymbol: delivery?.destinationSymbol,
    destinationFaAddress: delivery?.destinationFaAddress,
    destinationDecimals: delivery?.destinationDecimals,
    sweepable: delivery?.sweepable,
    amountFormatted: delivery?.amountOutFormatted ?? undefined,
    feeFormatted: delivery?.withdrawFeeFormatted ?? undefined,
    feeUsd: delivery?.withdrawFeeUsd ?? undefined,
    destinationTxHash: delivery?.destinationTxHash ?? undefined,
  } satisfies Partial<FunDepositTransfer>;
}

export function funCardFromItem(item: FunPaymentItem, subaccount: string): NewFunCard {
  return {
    type: "fun-deposit",
    sourceChain: "Fun",
    aliases: idsOf(item),
    subaccountToDepositTo: subaccount,
    destinationAddress: subaccount,
    destinationChain: "Aptos",
    currentStep: funCardStep(item),
    ...projectedFields(item),
  };
}

export type FunCardOp =
  | { kind: "add"; card: NewFunCard }
  | { kind: "replace"; oldId: string; card: NewFunCard }
  | { kind: "update"; id: string; update: Partial<FunDepositTransfer> }
  | { kind: "remove"; id: string };

export interface FunCardPlan {
  ops: FunCardOp[];
  imported: string[];
  importedDeliveries: string[];
}

function isFinished(item: FunPaymentItem): boolean {
  return isFinalFunPaymentState(item.state);
}

/** First sight of an item on this device: open, or finished recently with something left to do. */
function worthACard(item: FunPaymentItem, nowMs: number): boolean {
  if (!isFinished(item)) return true;
  if (nowMs - Date.parse(item.updatedAt) > FUN_IMPORT_WINDOW_MS) return false;
  return item.state !== "done" || item.nextAction === "confirm_deposit";
}

interface Ledgers {
  imported: ReadonlySet<string>;
  importedDeliveries: ReadonlySet<string>;
}

function matchingCards(
  funCards: readonly FunDepositTransfer[],
  ids: string[],
  taken: ReadonlySet<string>,
): FunDepositTransfer[] {
  return funCards.filter(
    (card) =>
      !taken.has(card.id) &&
      (ids.includes(card.paymentId) || card.aliases.some((alias) => ids.includes(alias))),
  );
}

function absorbedNearCard(
  item: FunPaymentItem,
  transfers: readonly TransferProgressData[],
): XchainDepositTransfer | undefined {
  const deliveryId = item.isDeliveryOwner ? item.delivery?.id : undefined;
  if (deliveryId === undefined) return undefined;
  return transfers.find(
    (t): t is XchainDepositTransfer => t.type === "xchain-deposit" && t.deliveryId === deliveryId,
  );
}

function carriedStep(absorbed: XchainDepositTransfer | undefined): TransferStep | undefined {
  return absorbed?.currentStep === "completed" ? "completed" : undefined;
}

function isDismissed(item: FunPaymentItem, ids: string[], imported: ReadonlySet<string>): boolean {
  // Confirm Deposit reopens a dismissed card; a swept delivery stays out via the delivery ledger.
  if (item.nextAction === "confirm_deposit" && item.delivery?.sweepable) return false;
  return item.provisional
    ? imported.has(funImportKey(item.id, true))
    : ids.some((id) => imported.has(funImportKey(id, false)));
}

function isSkipped(
  item: FunPaymentItem,
  ids: string[],
  absorbed: XchainDepositTransfer | undefined,
  ledgers: Ledgers,
  nowMs: number,
): boolean {
  const deliveryHandled =
    !absorbed && !!item.delivery && ledgers.importedDeliveries.has(item.delivery.id);
  if (isDismissed(item, ids, ledgers.imported) || deliveryHandled) return true;
  return !absorbed && !worthACard(item, nowMs);
}

function planKeeper(
  item: FunPaymentItem,
  ids: string[],
  matches: readonly FunDepositTransfer[],
  keeper: FunDepositTransfer,
  absorbed: XchainDepositTransfer | undefined,
): FunCardOp[] {
  const ops: FunCardOp[] = [];
  for (const card of matches) if (card !== keeper) ops.push({ kind: "remove", id: card.id });
  if (absorbed) ops.push({ kind: "remove", id: absorbed.id });

  const aliases = [...new Set([...keeper.aliases, ...ids])];
  const reported = absorbed?.reportedToWarehouse === true;
  const currentStep = funCardStep(item, carriedStep(absorbed) ?? keeper.currentStep);
  const changed =
    keeper.projection.version !== item.version ||
    keeper.paymentId !== item.id ||
    keeper.currentStep !== currentStep ||
    aliases.length !== keeper.aliases.length ||
    (reported && !keeper.reportedToWarehouse);
  if (changed) {
    ops.push({
      kind: "update",
      id: keeper.id,
      update: {
        ...projectedFields(item),
        aliases,
        currentStep,
        ...(reported && { reportedToWarehouse: true }),
      },
    });
  }
  return ops;
}

function planNewCard(
  item: FunPaymentItem,
  absorbed: XchainDepositTransfer | undefined,
  subaccount: string,
): FunCardOp {
  const card = {
    ...funCardFromItem(item, subaccount),
    currentStep: funCardStep(item, carriedStep(absorbed)),
    ...(absorbed?.reportedToWarehouse && { reportedToWarehouse: true }),
  };
  return absorbed ? { kind: "replace", oldId: absorbed.id, card } : { kind: "add", card };
}

/**
 * Turns the server's items into card writes. Cards match by any id an item was known by, so the
 * provisional card (and its place in the list) carries through to the evidence. An owned
 * delivery's NEAR card is folded in, so one payment never shows two cards.
 */
export function planFunCards(input: {
  items: readonly FunPaymentItem[];
  transfers: readonly TransferProgressData[];
  imported: ReadonlySet<string>;
  importedDeliveries: ReadonlySet<string>;
  nowMs: number;
  subaccount: string;
}): FunCardPlan {
  const { items, transfers, imported, importedDeliveries, nowMs, subaccount } = input;
  const plan: FunCardPlan = { ops: [], imported: [], importedDeliveries: [] };
  const funCards = transfers.filter((t): t is FunDepositTransfer => t.type === "fun-deposit");
  const ledgers: Ledgers = { imported, importedDeliveries };
  const taken = new Set<string>();

  const remember = (item: FunPaymentItem) => {
    const key = funImportKey(item.id, item.provisional);
    if (!imported.has(key)) plan.imported.push(key);
    const deliveryId = item.delivery?.id;
    if (deliveryId && !importedDeliveries.has(deliveryId)) plan.importedDeliveries.push(deliveryId);
  };

  for (const item of items) {
    const ids = idsOf(item);
    const matches = matchingCards(funCards, ids, taken);
    for (const card of matches) taken.add(card.id);
    const absorbed = absorbedNearCard(item, transfers);
    const keeper = matches.find((card) => card.paymentId === item.id) ?? matches[0];

    if (keeper) {
      plan.ops.push(...planKeeper(item, ids, matches, keeper, absorbed));
    } else {
      if (isSkipped(item, ids, absorbed, ledgers, nowMs)) continue;
      plan.ops.push(planNewCard(item, absorbed, subaccount));
    }
    remember(item);
  }

  return plan;
}
